/**
 * ErrorFilter - Centralized error filtering to prevent SDK self-reporting
 * 
 * This module prevents the BugMail SDK from capturing and reporting its own
 * internal errors, which would create infinite loops and noise in error logs.
 */

class ErrorFilter {
  constructor(config = {}) {
    this.config = config;
    
    // Patterns that identify SDK code in filenames and stack traces
    this.sdkPatterns = [
      '@bugmail-js',
      'bugmail-sdk',
      'bugmail-client',
      'bugmail-core',
      'network-manager',
      'error-processor',
      'context-collector',
      'error-filter'
    ];
    
    // Patterns that identify SDK messages in error messages and console logs
    this.sdkMessagePatterns = [
      '[BugMail',
      'BugMail Network',
      'BugMail SDK',
      'BugMail:'
    ];
  }

  /**
   * Determine if an error should be captured and sent to the backend
   * 
   * @param {Error|Object} error - Error object or error-like object to check
   * @param {Object} context - Additional context about the error
   * @param {boolean} context.isInternalError - Whether SDK is in internal error state
   * @returns {boolean} - True if error should be captured, false if it should be filtered
   */
  shouldCaptureError(error, context = {}) {
    // Don't capture if we're already in an internal error state
    if (context.isInternalError) {
      if (this.config.debug) {
        console.log('[BugMail Filter] Filtered: SDK in internal error state');
      }
      return false;
    }

    // Check error message for SDK patterns
    if (error.message && this.containsSdkPattern(error.message)) {
      if (this.config.debug) {
        console.log('[BugMail Filter] Filtered: SDK pattern in message:', error.message);
      }
      return false;
    }

    // Check stack trace for SDK patterns
    // Only filter if the ENTIRE stack is SDK code (not just part of it)
    if (error.stack && this.isEntirelySDKStack(error.stack)) {
      if (this.config.debug) {
        console.log('[BugMail Filter] Filtered: Entire stack is SDK code');
      }
      return false;
    }

    // Check filename if available (from ErrorEvent)
    if (error.filename && this.containsSdkPattern(error.filename)) {
      if (this.config.debug) {
        console.log('[BugMail Filter] Filtered: SDK pattern in filename:', error.filename);
      }
      return false;
    }

    // Error passed all filters - should be captured
    return true;
  }

  /**
   * Check if a string contains any SDK patterns
   * 
   * @param {string} str - String to check
   * @returns {boolean} - True if string contains SDK patterns
   */
  containsSdkPattern(str) {
    if (!str) return false;
    
    const lowerStr = str.toLowerCase();
    return this.sdkPatterns.some(pattern => 
      lowerStr.includes(pattern.toLowerCase())
    );
  }

  /**
   * Check if the ENTIRE stack trace is SDK code (no user code)
   * This is more precise than just checking if SDK patterns exist
   * 
   * @param {string} stack - Stack trace to check
   * @returns {boolean} - True if entire stack is SDK code
   */
  isEntirelySDKStack(stack) {
    if (!stack) return false;
    
    // Split stack into lines
    const lines = stack.split('\n').filter(line => line.trim());
    
    // Skip the first line (error message)
    const stackLines = lines.slice(1);
    
    if (stackLines.length === 0) return false;
    
    // Check if ALL stack frames contain SDK patterns
    const allSDK = stackLines.every(line => this.containsSdkPattern(line));
    
    return allSDK;
  }

  /**
   * Check if a console.error message should be captured
   * 
   * @param {Array} args - Arguments passed to console.error
   * @returns {boolean} - True if message should be captured
   */
  shouldCaptureConsoleError(args) {
    if (!args || !args.length) {
      return true;
    }

    const firstArg = args[0];
    
    // Check if it's an Error object with SDK in stack
    if (firstArg instanceof Error) {
      return this.shouldCaptureError(firstArg);
    }

    // Check if message contains SDK patterns
    const message = typeof firstArg === 'string' 
      ? firstArg 
      : JSON.stringify(firstArg);
    
    // Filter out messages with SDK message patterns
    const shouldFilter = this.sdkMessagePatterns.some(pattern => 
      message.includes(pattern)
    );

    if (shouldFilter && this.config.debug) {
      console.log('[BugMail Filter] Filtered console message:', message.substring(0, 100));
    }

    return !shouldFilter;
  }
}

export default ErrorFilter;
